import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Link,
  notFound,
  Outlet,
  redirect,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { Vazio } from "@/components/casca/Vazio";
import { primeiraArea, type AreaKey } from "@/lib/permissoes";
import { ErroApi } from "./api";
import { Casca } from "./casca/Casca";
import {
  agendaQuery,
  conexaoQuery,
  configuracoesQuery,
  conversaQuery,
  conversasQuery,
  empresaQuery,
  equipeQuery,
  euQuery,
  funilQuery,
  modelosQuery,
  pacientesQuery,
  queryClient,
  relatorioQuery,
  sistemaQuery,
} from "./consultas";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Agenda } from "./telas/Agenda";
import { Bancada } from "./telas/Bancada";
import { Conexao } from "./telas/Conexao";
import { Configuracoes } from "./telas/Configuracoes";
import { Conversa } from "./telas/Conversa";
import { Conversas, SemConversas } from "./telas/Conversas";
import { SECOES_EMPRESA, type SecaoEmpresa } from "@/components/casca/Ajustes";
import { Empresa } from "./telas/Empresa";
import { Equipe } from "./telas/Equipe";
import { Funil } from "./telas/Funil";
import { Modelos } from "./telas/Modelos";
import { Cadastro, Entrar, Inicio, RetornoDoGoogle } from "./telas/entrada/Entrada";
import { Onboarding } from "./telas/entrada/Onboarding";
import { Pacientes } from "./telas/Pacientes";
import { Relatorio } from "./telas/Relatorio";
import { Sistema } from "./telas/Sistema";

/**
 * O mapa do painel, e a regra que o faz não "se montar em blocos":
 *
 * CADA TELA DECLARA OS DADOS DE QUE PRECISA (`loader`), e o roteador só troca
 * de tela quando eles chegaram. Até lá, a tela anterior fica onde está. Com o
 * mouse em cima de um link, a busca já começa (`preload: "intent"`) — na
 * maioria dos cliques, os dados chegaram antes do clique.
 *
 * Os dados ficam guardados (`consultas.ts`): voltar a uma tela é instantâneo.
 */
export interface ContextoDoRoteador {
  auth: { entrou: boolean; clinica: string | null };
}

const raiz = createRootRouteWithContext<ContextoDoRoteador>()({
  component: Outlet,
  notFoundComponent: NaoEncontrada,
  errorComponent: Falha,
});

// ─────────────────── entrada ───────────────────

/** Quem já entrou não volta para a tela de login (a mesma regra do proxy do app antigo). */
const jaEntrou = ({ context }: { context: ContextoDoRoteador }) => {
  if (context.auth.entrou) throw redirect({ to: context.auth.clinica ? "/conversas" : "/onboarding" });
};

/**
 * A página inicial é pública: quem não entrou vê o que é o LummiChat (o Google
 * exige uma página inicial aberta para o "Continuar com o Google"); quem já
 * entrou vai direto para o painel, como sempre foi.
 */
const inicio = createRoute({ getParentRoute: () => raiz, path: "/", beforeLoad: jaEntrou, component: Inicio });

const entrar = createRoute({ getParentRoute: () => raiz, path: "/sign-in", beforeLoad: jaEntrou, component: Entrar });
const entrarGoogle = createRoute({
  getParentRoute: () => raiz,
  path: "/sign-in/sso-callback",
  component: () => <RetornoDoGoogle de="entrada" />,
});
const cadastro = createRoute({ getParentRoute: () => raiz, path: "/sign-up", beforeLoad: jaEntrou, component: Cadastro });
const cadastroGoogle = createRoute({
  getParentRoute: () => raiz,
  path: "/sign-up/sso-callback",
  component: () => <RetornoDoGoogle de="cadastro" />,
});

/**
 * Sem clínica ativa, é o primeiro passo de quem acabou de entrar. Com `?nova`,
 * é "criar OUTRA clínica", pedido de dentro do painel — e aí ter clínica ativa
 * é o normal, não motivo para mandar de volta. Até 23/09/2026 o link do
 * seletor de clínicas caía nessa volta e não criava nada.
 */
const onboarding = createRoute({
  getParentRoute: () => raiz,
  path: "/onboarding",
  validateSearch: (busca: Record<string, unknown>): { nova?: true } =>
    busca.nova === true || busca.nova === "true" || busca.nova === "1" ? { nova: true } : {},
  beforeLoad: ({ context, search }) => {
    if (!context.auth.entrou) throw redirect({ to: "/sign-in" });
    if (context.auth.clinica && !search.nova) throw redirect({ to: "/conversas" });
  },
  component: function RotaOnboarding() {
    const { nova } = onboarding.useSearch();
    return <Onboarding nova={Boolean(nova)} />;
  },
});

// ─────────────────── o painel ───────────────────

const app = createRoute({
  getParentRoute: () => raiz,
  id: "app",
  beforeLoad: ({ context, location }) => {
    if (!context.auth.entrou) throw redirect({ to: "/sign-in", search: { redirect_url: location.pathname } });
    if (!context.auth.clinica) throw redirect({ to: "/onboarding" });
  },
  loader: () => queryClient.ensureQueryData(euQuery()),
  component: Casca,
});

/**
 * A guarda de área NO NAVEGADOR — só para não desenhar uma tela que o
 * servidor vai recusar. Quem barra de verdade é a API (`exigirAreaDo`).
 */
function guarda(area: AreaKey) {
  return async () => {
    const eu = await queryClient.ensureQueryData(euQuery());
    if (!eu.areas.includes(area)) throw redirect({ to: primeiraArea(eu.areas) });
  };
}

const conversas = createRoute({
  getParentRoute: () => app,
  path: "/conversas",
  beforeLoad: guarda("conversas"),
  loader: () => queryClient.ensureQueryData(conversasQuery()),
  component: Conversas,
});

/** Como qualquer app de mensagem: abre no último assunto, não numa tela vazia. */
const conversasInicio = createRoute({
  getParentRoute: () => conversas,
  path: "/",
  loader: async () => {
    // No celular, /conversas É a lista (a seta de voltar da conversa traz para cá):
    // abrir a primeira conversa sozinho prenderia a pessoa dentro dela.
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) return;
    const lista = await queryClient.ensureQueryData(conversasQuery());
    const primeira = lista.aguardandoEquipe[0] ?? lista.atendendoVoce[0] ?? lista.comOAgente[0];
    if (primeira) throw redirect({ to: "/conversas/$telefone", params: { telefone: primeira.telefone }, replace: true });
  },
  component: SemConversas,
});

const conversa = createRoute({
  getParentRoute: () => conversas,
  path: "$telefone",
  loader: async ({ params }) => {
    try {
      return await queryClient.ensureQueryData(conversaQuery(params.telefone));
    } catch (e) {
      // Conversa que não existe nesta clínica: a tela diz isso NO LUGAR da
      // conversa, com a lista e o menu de pé. Um `notFound()` aqui subia até a
      // raiz e tirava o menu da tela — a pessoa ficava sem caminho de volta.
      if (e instanceof ErroApi && (e.status === 404 || e.status === 400)) return null;
      throw e;
    }
  },
  component: function RotaConversa() {
    const { telefone } = conversa.useParams();
    if (conversa.useLoaderData() === null) return <ConversaInexistente />;
    // `key`: outra conversa é outra tela — o texto digitado numa não pode
    // aparecer na caixa da outra.
    return <Conversa key={telefone} telefone={telefone} />;
  },
});

/** Uma área simples: guarda, dados e tela. As que têm algo a mais vêm logo abaixo. */
function area(caminho: string, chave: AreaKey, carregar: () => Promise<unknown>, Tela: () => ReactNode) {
  return createRoute({ getParentRoute: () => app, path: caminho, beforeLoad: guarda(chave), loader: carregar, component: Tela });
}

const buscar = queryClient.ensureQueryData.bind(queryClient);

const pacientes = area("/pacientes", "pacientes", () => buscar(pacientesQuery()), Pacientes);
const agenda = area("/agenda", "agenda", () => buscar(agendaQuery()), Agenda);
const relatorioRota = area("/relatorio", "relatorio", () => buscar(relatorioQuery()), Relatorio);
const modelos = area("/modelos", "modelos", () => buscar(modelosQuery()), Modelos);
/** A Empresa em páginas curtas (Ajustes › A clínica): a seção fica na URL, o formulário é um só. */
const empresa = createRoute({
  getParentRoute: () => app,
  path: "/empresa",
  validateSearch: (busca: Record<string, unknown>): { secao?: SecaoEmpresa } =>
    SECOES_EMPRESA.includes(busca.secao as SecaoEmpresa) ? { secao: busca.secao as SecaoEmpresa } : {},
  beforeLoad: guarda("empresa"),
  loader: () => queryClient.ensureQueryData(empresaQuery()),
  component: function RotaEmpresa() {
    const { secao } = empresa.useSearch();
    return <Empresa secao={secao ?? "clinica"} />;
  },
});
const conexao = area("/conexao", "conexao", () => buscar(conexaoQuery()), Conexao);
const equipe = area("/equipe", "equipe", () => buscar(equipeQuery()), Equipe);
const configuracoes = area("/configuracoes", "configuracoes", () => buscar(configuracoesQuery()), Configuracoes);

/** Barras ou tabela: a escolha fica na URL, para voltar e compartilhar do mesmo jeito. */
const funilRota = createRoute({
  getParentRoute: () => app,
  path: "/funil",
  validateSearch: (busca: Record<string, unknown>): { ver?: "tabela" } => (busca.ver === "tabela" ? { ver: "tabela" } : {}),
  beforeLoad: guarda("funil"),
  loader: () => queryClient.ensureQueryData(funilQuery()),
  component: function RotaFunil() {
    const { ver } = funilRota.useSearch();
    return <Funil verTabela={ver === "tabela"} />;
  },
});

/** Só de quem opera a plataforma. Para os outros, a tela não existe — nem 403, "não achei". */
const sistema = createRoute({
  getParentRoute: () => app,
  path: "/sistema",
  beforeLoad: async () => {
    if (!(await queryClient.ensureQueryData(euQuery())).donoDaPlataforma) throw notFound();
  },
  loader: () => queryClient.ensureQueryData(sistemaQuery()),
  component: Sistema,
});

/** Só administrador: a bancada fala com o modelo de verdade, na conta de quem paga pelos tokens. */
const bancada = createRoute({
  getParentRoute: () => app,
  path: "/sandbox",
  beforeLoad: async () => {
    if ((await queryClient.ensureQueryData(euQuery())).papel !== "admin") throw notFound();
  },
  component: function RotaBancada() {
    const { data: eu } = useSuspenseQuery(euQuery());
    return <Bancada slug={eu.clinica.slug} nome={eu.clinica.nome} />;
  },
});

export const arvoreDeRotas = raiz.addChildren([
  inicio,
  entrar,
  entrarGoogle,
  cadastro,
  cadastroGoogle,
  onboarding,
  app.addChildren([
    conversas.addChildren([conversasInicio, conversa]),
    pacientes,
    agenda,
    funilRota,
    relatorioRota,
    modelos,
    empresa,
    conexao,
    equipe,
    configuracoes,
    sistema,
    bancada,
  ]),
]);

export const router = createRouter({
  routeTree: arvoreDeRotas,
  context: { auth: { entrou: false, clinica: null } },
  defaultPreload: "intent",
  // Quem decide se o dado está velho é o cache (consultas.ts), não o roteador.
  defaultPreloadStaleTime: 0,
  // A tela anterior fica até a nova estar pronta. Só depois de 10s de espera
  // algo mais aparece no lugar — e a essa altura é problema, não espera.
  defaultPendingMs: 10_000,
  defaultPendingComponent: () => null,
});

// ─────────────────── quando algo dá errado ───────────────────

function ConversaInexistente() {
  return (
    <div className="bg-chat flex flex-1 items-center justify-center">
      <Vazio icone="conversas" titulo="Não achei esta conversa" acao={<Link to="/conversas" className="flex h-9 items-center rounded-full bg-[var(--color-cartao)] px-4 text-[13px] font-semibold shadow-botao hover:bg-[var(--color-painel)]">Ver as conversas</Link>}>
        O número pode estar errado, ou esta conversa é de outra clínica.
      </Vazio>
    </div>
  );
}

function NaoEncontrada() {
  return (
    <div className="flex h-full min-h-[60vh] flex-1 items-center justify-center">
      <Vazio icone="info" titulo="Não achei esta página">
        O endereço pode ter mudado, ou esta conversa não existe nesta clínica.
      </Vazio>
    </div>
  );
}

function Falha({ error, reset }: ErrorComponentProps) {
  const roteador = useRouter();
  const sessao = error instanceof ErroApi && error.status === 401;
  const semClinica = error instanceof ErroApi && error.status === 409;

  // Sessão vencida não é erro para mostrar: é voltar para o login.
  useEffect(() => {
    if (sessao) window.location.href = "/sign-in";
    if (semClinica) window.location.href = "/onboarding";
  }, [sessao, semClinica]);
  if (sessao || semClinica) return null;

  return (
    <div className="flex h-full min-h-[60vh] flex-1 items-center justify-center">
      <Vazio
        icone="alerta"
        titulo="Não consegui carregar esta tela"
        acao={
          <button
            type="button"
            onClick={() => {
              reset();
              void roteador.invalidate();
            }}
            className="flex h-9 items-center rounded-full bg-[var(--color-marca)] px-4 text-[13px] font-semibold text-[var(--color-sobre-marca)] hover:brightness-110"
          >
            Tentar de novo
          </button>
        }
      >
        {error instanceof Error ? error.message : "Erro inesperado."}
      </Vazio>
    </div>
  );
}
